import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Flame, Loader2, Check } from "lucide-react";

export default function WellOfSoulsModal({ open, onClose, onChanged }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { setState(await api.get("/souls")); } catch { /* noop */ }
  };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const buy = async (blessing_key) => {
    setBusy(blessing_key);
    try {
      await api.post("/souls/buy", { blessing_key });
      toast.success("The Ember accepts your offering.");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The Well refused your offering.");
    } finally { setBusy(null); }
  };

  return (
    <div
      className="fixed inset-0 z-[210] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="well-of-souls-modal"
      onClick={onClose}
    >
      <div className="panel p-8 max-w-3xl w-full relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 ash-text hover:text-white" data-testid="well-close-btn">
          <X size={18} />
        </button>
        <div className="flex items-start justify-between gap-4 flex-col md:flex-row md:items-end">
          <div>
            <p className="text-xs tracking-[0.4em] ember">THE WELL OF SOULS</p>
            <h3 className="font-heading text-4xl mt-2 text-[#f0e6d2]">Spend the dead. <span className="italic gold-text">Buy tomorrow.</span></h3>
            <p className="text-sm ash-text italic mt-1">Blessings persist across every run.</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] ash-text">SOULS</div>
            <div className="font-heading text-3xl ember flex items-center gap-1" data-testid="souls-balance">
              <Flame size={16} />{state?.souls ?? "..."}
            </div>
          </div>
        </div>

        {!state ? (
          <div className="mt-6 text-center ash-text">
            <Loader2 size={16} className="inline animate-spin mr-2" /> Drawing water from the well...
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
            {state.catalog.map((b) => {
              const owned = state.owned[b.key] || 0;
              const maxed = owned >= b.max_stack;
              const canAfford = state.souls >= b.cost;
              return (
                <div key={b.key} className="panel p-4 flex flex-col" data-testid={`blessing-${b.key}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-heading text-lg text-[#f0e6d2]">{b.name}</div>
                    <div className="text-[10px] tracking-widest ash-text">
                      {owned}/{b.max_stack}{owned > 0 && <span className="gold-text ml-1">◆</span>}
                    </div>
                  </div>
                  <p className="text-xs ash-text mt-1 italic">{b.desc}</p>
                  <div className="mt-auto pt-3 flex items-center justify-between">
                    <span className="ember text-sm flex items-center gap-1"><Flame size={12}/> {b.cost}</span>
                    <button
                      disabled={busy !== null || maxed || !canAfford}
                      onClick={() => buy(b.key)}
                      data-testid={`buy-blessing-${b.key}-btn`}
                      className="btn-glow py-2 text-xs"
                    >
                      {maxed ? <><Check size={12}/> MAX</> : busy === b.key ? "..." : "BUY"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
